import { TableAbstract, TextFilter } from "shared/components/Components";
import { useMercuryContext } from "../../context";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import TimezoneConverter from "components/timezone-converter";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import WorklogDefinitionForm from "./WorklogDefinitionForm";
import { Space, Tag } from "antd";
import PermissionWrapper from "shared/components/PermissionWrapper";
import { checkPermissions } from "common/utils/checkPermissions";
import { SwitchFilter } from "stories/filters/switch-filter/SwitchFilter";
import { descriptionColumn } from "shared/table/columnsConfig";
import BooleanWithoutNotSet from "../../filters/BooleanWithoutNotSet.filter";
import TargetEntityFilter from "../../filters/TargetEntity.filter";
import ProjectTypeFilter from "../../filters/ProjectType.filter";

class WorklogDefinitions extends TableAbstract<any> {
  defaultSortField = "name";

  FilterComponents = [
    {
      id: "active",
      name: "active",
      title: "Active",
      component: BooleanWithoutNotSet,
      active: true,
    },
    {
      id: "description",
      name: "description",
      title: "Description",
      component: TextFilter,
      active: true,
    },
    {
      id: "includeDeleted",
      name: "deleted",
      title: "Deleted",
      component: SwitchFilter,
      active: true,
    },
    {
      id: "name",
      name: "name",
      title: "Name",
      component: TextFilter,
      active: true,
    },
    {
      id: "projectTypes",
      name: "projectTypes",
      title: "Project Types",
      component: ProjectTypeFilter,
      active: true,
    },
    {
      id: "targetEntity",
      name: "targetEntity",
      title: "Target Entity",
      component: TargetEntityFilter,
      active: true,
    },
  ];

  columns = [
    {
      id: "name",
      title: "Name",
      dataIndex: "name",
      sorter: true,
      fixed: undefined,
      width: 160,
    },
    descriptionColumn,
    {
      id: "dbKey",
      title: "DB Key",
      dataIndex: "dbKey",
      sorter: true,
      fixed: undefined,
      width: 180,
    },
    {
      id: "targetEntity",
      title: "Target Entity",
      dataIndex: "targetEntity",
      sorter: true,
      fixed: undefined,
      width: 130,
    },
    {
      id: "projectTypes",
      title: "Project Types",
      dataIndex: "projectTypes",
      sorter: true,
      fixed: undefined,
      width: 150,
      render: (text: string | string[]) => this.renderTags(text),
    },
    {
      id: "createdAt",
      title: "Creation Timestamp",
      dataIndex: "createdAt",
      fixed: undefined,
      width: 160,
      render: (text: string) => <TimezoneConverter date={text} />,
    },
    {
      id: "deleted",
      title: "Deleted",
      dataIndex: "deleted",
      width: 75,
      fixed: undefined,
      render: (text: string, record: any) =>
        record.deleted ? (
          <CheckOutlined style={{ color: "var(--tertiary-color)" }} />
        ) : (
          <CloseOutlined style={{ color: "var(--red)" }} />
        ),
    },
    {
      id: "actions",
      title: "Actions",
      width: 160,
      render: (_: any, record: any) => {
        const userPermissions = this.userContext.userDetails?.permissions || [];
        const canEdit =
            checkPermissions(userPermissions, [
          {
            customerApplication: "Mercury",
            permissionName: "Worklog Definitions",
            action: "edit",
          },
        ]);
        const canDelete =
            checkPermissions(userPermissions, [
          {
            customerApplication: "Mercury",
            permissionName: "Worklog Definitions",
            action: "delete",
          },
        ]);
        return record?.deleted ? (
          <PermissionWrapper
            hasPermissions={canEdit}
            children={
              <WorklogDefinitionForm
                disabled={!canEdit}
                mode="restore"
                editData={record}
                refreshList={() => this.reload()}
              />
            }
          />
        ) : (
          <Space align="center">
            <PermissionWrapper
              hasPermissions={canEdit}
              children={
                <WorklogDefinitionForm
                  mode="edit"
                  editData={record}
                  refreshList={() => this.reload()}
                  disabled={!canEdit}
                />
              }
            />
            <PermissionWrapper
              hasPermissions={canDelete}
              children={
                <WorklogDefinitionForm
                  mode="delete"
                  editData={record}
                  refreshList={() => this.reload()}
                  disabled={!canDelete}
                />
              }
            />
          </Space>
        );
      },
    },
  ];

  renderTags(text: string | string[]) {
    if (typeof text === "string") {
      return (
        <Tag
          color="var(--tertiary-color)"
          style={{ margin: "0 2px 2px 0", padding: "0 4px" }}
        >
          {text}
        </Tag>
      );
    }

    return text?.map((item, index) => (
      <Tag
        key={index}
        color="var(--tertiary-color)"
        style={{ margin: "0 2px 2px 0", padding: "0 4px" }}
      >
        {item}
      </Tag>
    ));
  }

  renderActions() {
    const userPermissions = this.userContext.userDetails?.permissions || [];
    const canCreate =
        checkPermissions(userPermissions, [
      {
        customerApplication: "Mercury",
        permissionName: "Worklog Definitions",
        action: "create",
      },
    ]);

    return (
      <Space size="small">
        <PermissionWrapper
          hasPermissions={canCreate}
          children={
            <WorklogDefinitionForm
              disabled={!canCreate}
              refreshList={() => this.reload()}
            />
          }
        />
      </Space>
    );
  }

  rowClassName = (record: any): string => {
    return "row";
  };

  async fetch() {
    const { sortOrder, sortField } = this.state;
    const filters = this.filtersToQuery();

    const res = await this.userContext.invoicingClient.post(
      `/invoicing-v1/worklog-type-definitions/search`,
      {
        filter: {
          deleted: filters.deleted ?? false,
          ...filters,
        },
        sort: {
          [sortField || "name"]: sortOrder === "ascend" ? 1 : -1,
        },
        pagination: {
          pageNumber: this.currentPage || 1,
          resultsPerPage: this.itemsPerPage,
        },
      }
    );

    return { data: res.data } as any;
  }

  pageTitle = "Manage Worklog Definitions - Mercury";
  title = "Worklog Definitions";
  pageClass = "worklogDefinitionsTable";
}

export default function ManageWorklogDefinitions() {
    const
    context = useMercuryContext(),
    breaks = useBreakpoint();

  return (
    //@ts-ignore
    <WorklogDefinitions
      id={"ManageWorklogDefinitions"}
      context={context as any}
      useLegacyEndpoints={false}
      breaks={breaks as any}
      hasSidebar={true}
      noSelect
      displayHideFilters={true}
    />
  );
}
